import { CalendarDays } from 'lucide-react'
import { assetUrl } from '../../lib/http'
import { pkr } from '../../utils/format'
import { Pill } from '../../components/account/AccountUi'
import { INSTALMENT_STATUS, day } from '../../components/account/status'
import { EmptyAccount, Loading, Problem } from '../../components/account/AccountStates'
import { useMyInstalments } from '../../hooks/useAccountQueries'
import type { AccountInstalment } from '../../data/account'

/* The bank calendar, from the buyer's side.

   One card per car on a plan: the terms across the top, a bar showing how
   far through it is, and every month underneath — paid, due, overdue or
   still to come. Nothing here is stored as a status except "paid"; the rest
   is the calendar read against today, which is why this page is always
   right without anybody updating it.

   Who paid a month is on the row. It is often the buyer, and a person
   checking whether their own transfer was recorded should be able to see
   that it was, on the month it went against. */

function months(count: number) {
  return `${count} ${count === 1 ? 'month' : 'months'}`
}

function PlanCard({ entry }: { entry: AccountInstalment }) {
  const { vehicle, plan, schedule } = entry
  const percent = Math.round(plan.progress * 100)

  return (
    <article className="overflow-hidden rounded-2xl border border-line bg-raised/60">
      <div className="flex flex-wrap items-start gap-5 p-5 sm:p-6">
        <span className="block h-20 w-28 shrink-0 overflow-hidden rounded-xl bg-surface">
          {vehicle.image ? (
            <img
              src={assetUrl(vehicle.image)}
              alt=""
              loading="lazy"
              className="size-full object-contain p-1"
            />
          ) : null}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <h3 className="font-display text-[17px] font-semibold tracking-[-0.02em] text-platinum">
              {vehicle.title}
            </h3>
            {plan.settled ? (
              <Pill tone="positive">Settled</Pill>
            ) : plan.overdue.count > 0 ? (
              <Pill tone="bad">
                {months(plan.overdue.count)} overdue
              </Pill>
            ) : plan.nextDue ? (
              <Pill tone="waiting">Next on {day(plan.nextDue.dueOn)}</Pill>
            ) : null}
          </div>

          <p className="mt-1 text-[13px] text-dim">
            {vehicle.year} · PKR {pkr.format(plan.monthly)} a month for {plan.years} years, from{' '}
            {day(plan.bookedOn)}
          </p>

          {/* How far through, as a bar and as a sentence. The bar alone is
              not a figure anybody can act on. */}
          <div className="mt-4">
            <div
              role="progressbar"
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${plan.paid.count} of ${plan.months} months paid`}
              className="h-1.5 w-full overflow-hidden rounded-full bg-surface"
            >
              <span
                className="block h-full rounded-full bg-lime transition-[width] duration-500"
                style={{ width: `${percent}%` }}
              />
            </div>
            <p className="mt-2 text-[12.5px] text-steel tabular-nums">
              {plan.paid.count} of {plan.months} months paid · PKR{' '}
              {pkr.format(plan.unpaid.amount)} still to run
            </p>
          </div>
        </div>
      </div>

      {/* The months. Scrolled rather than paged: a plan is three to ten
          years, and somebody looking for one month wants to run their eye
          down the column rather than click through it. */}
      <div className="max-h-[380px] overflow-y-auto border-t border-line">
        <table className="w-full border-collapse text-left">
          <thead className="sticky top-0 bg-raised">
            <tr className="border-b border-line">
              <th scope="col" className="label-sm px-5 py-3 text-steel">
                Month
              </th>
              <th scope="col" className="label-sm px-5 py-3 text-steel">
                Due
              </th>
              <th scope="col" className="label-sm px-5 py-3 text-right text-steel">
                Amount
              </th>
              <th scope="col" className="label-sm px-5 py-3 text-right text-steel">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {schedule.map((row) => {
              const status = INSTALMENT_STATUS[row.status] ?? INSTALMENT_STATUS.upcoming

              return (
                <tr key={row.sequence} className="border-b border-line-soft last:border-0">
                  <td className="px-5 py-3 text-[13.5px] text-platinum tabular-nums">
                    {row.sequence}
                    <span className="text-dim"> / {row.of}</span>
                  </td>
                  <td className="px-5 py-3 text-[13.5px] whitespace-nowrap text-steel">
                    {day(row.dueOn)}
                  </td>
                  <td className="px-5 py-3 text-right text-[13.5px] text-platinum tabular-nums">
                    {pkr.format(row.status === 'paid' ? row.paidAmount : row.amount)}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <Pill tone={status.tone}>{status.label}</Pill>
                    {row.status === 'paid' && row.paidOn ? (
                      <span className="mt-1 block text-[11.5px] text-dim">
                        {day(row.paidOn)}
                        {row.paidBy === 'client' ? ' · by you' : ' · by us'}
                      </span>
                    ) : null}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </article>
  )
}

export function AccountInstalments() {
  const query = useMyInstalments()

  if (query.isPending) return <Loading label="Loading your bank plans" />
  if (query.isError) return <Problem onRetry={() => void query.refetch()} />

  const plans = query.data ?? []

  if (plans.length === 0) {
    return (
      <EmptyAccount
        icon={<CalendarDays className="size-5" strokeWidth={1.8} />}
        title="No bank plan running"
        body="If you take a car on bank instalments, every month of the plan appears here — what is due, what has been paid, and by whom."
      />
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {plans.map((entry) => (
        <PlanCard key={entry.vehicle.id} entry={entry} />
      ))}
    </div>
  )
}

export default AccountInstalments
